import * as React from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Badge,
  Button,
  Dialog,
  FormField,
  Input,
  Select,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  formatDateTimeVn,
  useToast,
} from "@chotca/ui";
import { Pencil, RotateCcw, Send, Sparkles } from "lucide-react";
import {
  useAdminNotificationTemplates,
  useResetAdminNotificationTemplate,
  useSendAdminNotification,
  useUpdateAdminNotificationTemplate,
} from "@chotca/api-client";
import type {
  AdminNotificationRecipientSelector,
  AdminSendNotificationRequest,
  NotificationTemplate,
  NotificationTypeValue,
} from "@chotca/config";
import { useTabParam } from "@/lib/use-tab-param";

const SEGMENT_OPTIONS: Array<{
  value: "ALL" | "WORKER" | "EMPLOYER" | "ADMIN";
  label: string;
}> = [
  { value: "WORKER", label: "Tất cả người lao động" },
  { value: "EMPLOYER", label: "Tất cả nhà tuyển dụng" },
  { value: "ALL", label: "Tất cả người dùng (worker + employer)" },
  { value: "ADMIN", label: "Tất cả admin" },
];

export function NotificationsClient() {
  const [tab, setTab] = useTabParam("send");
  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-ink">Thông báo đẩy</h1>
        <p className="mt-1 text-sm text-ash">
          Gửi push tuỳ chỉnh cho người dùng và chỉnh sửa nội dung các mẫu thông
          báo hệ thống.
        </p>
      </header>

      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="send">Gửi thông báo</TabsTrigger>
          <TabsTrigger value="templates">Mẫu hệ thống</TabsTrigger>
        </TabsList>
        <TabsContent value="send">
          <SendSection />
        </TabsContent>
        <TabsContent value="templates">
          <TemplatesSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Send custom push
// ---------------------------------------------------------------------------

type Mode = "segment" | "identifiers";

function parseIdentifiers(text: string): string[] {
  return text
    .split(/[\n,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

const sendSchema = z
  .object({
    mode: z.enum(["segment", "identifiers"]),
    segment: z.enum(["ALL", "WORKER", "EMPLOYER", "ADMIN"]),
    identifiersText: z.string(),
    title: z
      .string()
      .trim()
      .min(1, "Vui lòng nhập tiêu đề")
      .max(160, "Tiêu đề tối đa 160 ký tự"),
    body: z
      .string()
      .trim()
      .min(1, "Vui lòng nhập nội dung")
      .max(1000, "Nội dung tối đa 1000 ký tự"),
    actionUrl: z.string().trim().max(500, "Deep link tối đa 500 ký tự"),
  })
  .superRefine((v, ctx) => {
    if (
      v.mode === "identifiers" &&
      parseIdentifiers(v.identifiersText).length === 0
    ) {
      ctx.addIssue({
        path: ["identifiersText"],
        code: z.ZodIssueCode.custom,
        message: "Cần ít nhất 1 email hoặc số điện thoại",
      });
    }
  });
type SendValues = z.infer<typeof sendSchema>;

function SendSection() {
  const { toast } = useToast();
  const send = useSendAdminNotification();

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<SendValues>({
    resolver: zodResolver(sendSchema),
    mode: "onTouched",
    defaultValues: {
      mode: "segment",
      segment: "WORKER",
      identifiersText: "",
      title: "",
      body: "",
      actionUrl: "",
    },
  });

  const mode = watch("mode") as Mode;
  const title = watch("title");
  const body = watch("body");

  const onSubmit = handleSubmit(async (values) => {
    const selector: AdminNotificationRecipientSelector =
      values.mode === "segment"
        ? { mode: "segment", segment: values.segment }
        : {
            mode: "identifiers",
            identifiers: parseIdentifiers(values.identifiersText),
          };
    const payload: AdminSendNotificationRequest = {
      title: values.title,
      body: values.body,
      actionUrl: values.actionUrl || null,
      recipients: [selector],
    };
    try {
      const res = await send.mutateAsync(payload);
      toast({
        tone: "success",
        title: `Đã gửi tới ${res.deliveredCount}/${res.resolvedUserCount} người dùng`,
      });
      reset({
        mode: values.mode,
        segment: values.segment,
        identifiersText: "",
        title: "",
        body: "",
        actionUrl: "",
      });
    } catch (err) {
      toast({
        tone: "error",
        title: "Gửi thất bại",
        description: (err as Error).message,
      });
    }
  });

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      className="space-y-5 rounded-2xl border border-smoke bg-paper p-5"
    >
      <div className="space-y-3">
        <h2 className="text-sm font-bold uppercase text-ash">Người nhận</h2>
        <div className="flex flex-wrap gap-2">
          <ModeChip
            active={mode === "segment"}
            onClick={() => setValue("mode", "segment")}
          >
            Theo nhóm
          </ModeChip>
          <ModeChip
            active={mode === "identifiers"}
            onClick={() => setValue("mode", "identifiers")}
          >
            Theo email / số điện thoại
          </ModeChip>
        </div>

        {mode === "segment" ? (
          <FormField label="Nhóm">
            <Controller
              control={control}
              name="segment"
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  options={SEGMENT_OPTIONS}
                />
              )}
            />
          </FormField>
        ) : (
          <FormField
            label="Danh sách email / số điện thoại"
            description="Mỗi dòng một giá trị, hoặc ngăn cách bằng dấu phẩy."
            error={errors.identifiersText?.message}
          >
            <Textarea
              rows={5}
              placeholder={"+84901234567\nuser@example.com"}
              invalid={!!errors.identifiersText}
              {...register("identifiersText")}
            />
          </FormField>
        )}
      </div>

      <div className="space-y-3">
        <h2 className="text-sm font-bold uppercase text-ash">Nội dung</h2>
        <FormField label="Tiêu đề" error={errors.title?.message}>
          <Input
            maxLength={160}
            invalid={!!errors.title}
            {...register("title")}
          />
        </FormField>
        <FormField label="Nội dung" error={errors.body?.message}>
          <Textarea
            rows={4}
            maxLength={1000}
            invalid={!!errors.body}
            {...register("body")}
          />
        </FormField>
        <FormField
          label="Deep link"
          optional
          description='Đường dẫn tương đối, ví dụ "/profile" hoặc "/posting/abc123".'
          error={errors.actionUrl?.message}
        >
          <Input
            placeholder="/"
            invalid={!!errors.actionUrl}
            {...register("actionUrl")}
          />
        </FormField>
      </div>

      <div className="flex items-center justify-between gap-3 pt-2">
        <PushPreview title={title} body={body} />
        <Button type="submit" loading={send.isPending}>
          <Send size={16} /> Gửi
        </Button>
      </div>
    </form>
  );
}

function ModeChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors " +
        (active
          ? "border-ink bg-ink text-paper"
          : "border-smoke bg-paper text-ash hover:text-ink")
      }
    >
      {children}
    </button>
  );
}

function PushPreview({ title, body }: { title: string; body: string }) {
  if (!title && !body)
    return <span className="text-xs text-ash">Preview xuất hiện ở đây.</span>;
  return (
    <div className="max-w-xs rounded-2xl border border-smoke bg-mist px-3 py-2 shadow-sm">
      <p className="text-[11px] font-bold uppercase text-ash">Chốt Ca</p>
      <p className="truncate text-sm font-semibold text-ink">
        {title || "Tiêu đề"}
      </p>
      <p className="line-clamp-2 text-xs text-ink/80">{body || "Nội dung"}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

function TemplatesSection() {
  const { data, isLoading } = useAdminNotificationTemplates();
  const [editing, setEditing] = React.useState<NotificationTemplate | null>(
    null,
  );
  const items = data?.items ?? [];

  if (isLoading) {
    return (
      <div className="rounded-2xl border border-smoke bg-paper p-5 text-sm text-ash">
        Đang tải…
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {items.map((t) => (
          <TemplateRow key={t.type} template={t} onEdit={() => setEditing(t)} />
        ))}
      </div>
      {editing ? (
        <EditTemplateDialog
          template={editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </>
  );
}

function TemplateRow({
  template,
  onEdit,
}: {
  template: NotificationTemplate;
  onEdit: () => void;
}) {
  return (
    <div className="flex items-start gap-4 rounded-2xl border border-smoke bg-paper p-4">
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-[11px] font-semibold uppercase text-ash">
            {template.type}
          </span>
          {template.isCustomised ? (
            <Badge tone="sun">Đã sửa</Badge>
          ) : (
            <Badge tone="smoke">Mặc định</Badge>
          )}
        </div>
        <p className="text-sm font-semibold text-ink">{template.title}</p>
        <p className="line-clamp-2 text-xs text-ash">{template.body}</p>
        {template.description ? (
          <p className="mt-1 text-[11px] italic text-ash">
            {template.description}
          </p>
        ) : null}
        <p className="text-[11px] text-ash">
          Cập nhật: {formatDateTimeVn(template.updatedAt)}
        </p>
      </div>
      <Button variant="ghost" onClick={onEdit}>
        <Pencil size={14} /> Sửa
      </Button>
    </div>
  );
}

const templateSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, "Vui lòng nhập tiêu đề")
    .max(160, "Tiêu đề tối đa 160 ký tự"),
  body: z
    .string()
    .trim()
    .min(1, "Vui lòng nhập nội dung")
    .max(1000, "Nội dung tối đa 1000 ký tự"),
  actionUrlTemplate: z.string().trim().max(500, "Deep link tối đa 500 ký tự"),
});
type TemplateValues = z.infer<typeof templateSchema>;

function EditTemplateDialog({
  template,
  onClose,
}: {
  template: NotificationTemplate;
  onClose: () => void;
}) {
  const type = template.type as NotificationTypeValue;
  const update = useUpdateAdminNotificationTemplate(type);
  const resetTemplate = useResetAdminNotificationTemplate(type);
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TemplateValues>({
    resolver: zodResolver(templateSchema),
    mode: "onTouched",
    defaultValues: {
      title: template.title,
      body: template.body,
      actionUrlTemplate: template.actionUrlTemplate ?? "",
    },
  });

  const onSave = handleSubmit(async (values) => {
    try {
      await update.mutateAsync({
        title: values.title,
        body: values.body,
        actionUrlTemplate: values.actionUrlTemplate || null,
      });
      toast({ tone: "success", title: "Đã lưu mẫu" });
      onClose();
    } catch (err) {
      toast({
        tone: "error",
        title: "Lưu thất bại",
        description: (err as Error).message,
      });
    }
  });

  async function onReset() {
    try {
      const fresh = await resetTemplate.mutateAsync();
      reset({
        title: fresh.title,
        body: fresh.body,
        actionUrlTemplate: fresh.actionUrlTemplate ?? "",
      });
      toast({ tone: "success", title: "Đã khôi phục mẫu mặc định" });
    } catch (err) {
      toast({
        tone: "error",
        title: "Khôi phục thất bại",
        description: (err as Error).message,
      });
    }
  }

  const variables = template.variables ?? [];

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title={`Mẫu: ${template.type}`}
      description={template.description ?? undefined}
      footer={
        <div className="flex items-center justify-between gap-2">
          <Button
            variant="ghost"
            onClick={onReset}
            loading={resetTemplate.isPending}
          >
            <RotateCcw size={14} /> Khôi phục mặc định
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              Huỷ
            </Button>
            <Button onClick={onSave} loading={update.isPending}>
              Lưu
            </Button>
          </div>
        </div>
      }
    >
      <form className="space-y-4" onSubmit={onSave} noValidate>
        {variables.length > 0 ? (
          <div className="rounded-xl bg-mist p-3 text-xs text-ash">
            <p className="mb-1 flex items-center gap-1 font-semibold text-ink">
              <Sparkles size={12} /> Biến có sẵn
            </p>
            <div className="flex flex-wrap gap-1.5">
              {variables.map((v) => (
                <code
                  key={v}
                  className="rounded bg-paper px-1.5 py-0.5 font-mono text-[11px] text-ink"
                >{`{{${v}}}`}</code>
              ))}
            </div>
          </div>
        ) : null}
        <FormField label="Tiêu đề" error={errors.title?.message}>
          <Input
            maxLength={160}
            invalid={!!errors.title}
            {...register("title")}
          />
        </FormField>
        <FormField label="Nội dung" error={errors.body?.message}>
          <Textarea
            rows={4}
            maxLength={1000}
            invalid={!!errors.body}
            {...register("body")}
          />
        </FormField>
        <FormField
          label="Deep link"
          description="Có thể chứa biến, ví dụ /posting/{{postingId}}"
          error={errors.actionUrlTemplate?.message}
        >
          <Input
            invalid={!!errors.actionUrlTemplate}
            {...register("actionUrlTemplate")}
          />
        </FormField>
        <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
      </form>
    </Dialog>
  );
}
